import 'reflect-metadata';
import {inject, injectable} from 'inversify';
import {ChannelProvider} from 'openfin/_v2/api/interappbus/channel/provider';

import {Injector} from './common/Injector';
import {Inject} from './common/Injectables';
import {ServiceStore} from './store/ServiceStore';
import {WebWindowFactory} from './model/WebWindow';
import {Monitor} from './model/Monitor';
import {LaunchBar} from './controller/LaunchBar';
import {Workspaces} from './controller/Workspaces';
import {Authentication} from './controller/Authentication';
import {Settings} from './controller/Settings';
import {Directory} from './controller/Directory';
import {initConfig} from './utils/processHelpers';
import {StartAuthentication} from './store/partials/Authentication';
import {ADMIN_UPDATE_CHANNEL, UpdateTopics} from './constants';
import {RefreshOrganization} from './store/partials/Organization';
import {UpdateDirectory} from './store/partials/Directory';
import {API} from './model/API';
import {App} from './types/commons';
import {AdminData} from './model/api/Admin';
import {AuthData} from './model/api/Auth';
import {MiscData} from './model/api/Misc';
import {UserData} from './model/api/User';
import {ChannelManager} from './controller/ChannelManager';
import {GetSearchEngines} from './store/partials/SearchEngines';

@injectable()
export class Main {
    // Updates made in the Admin panel
    private _updateChannel!: ChannelProvider;

    constructor(
        @inject(Inject.API) private readonly api: API,
        @inject(Inject.AUTHENTICATION) private readonly authentication: Authentication,
        @inject(Inject.DIRECTORY) private readonly directory: Directory,
        @inject(Inject.CHANNEL_MANAGER) private readonly channelManager: ChannelManager,
        @inject(Inject.LAUNCHBAR) private readonly launchBar: LaunchBar,
        @inject(Inject.MONITOR) private readonly monitorModel: Monitor,
        @inject(Inject.SETTINGS) private readonly settings: Settings,
        @inject(Inject.STORE) private readonly preduxStore: ServiceStore,
        @inject(Inject.WEB_WINDOW_FACTORY) private readonly webWindowFactory: WebWindowFactory,
        @inject(Inject.WORKSPACES) private readonly workspaces: Workspaces
    ) {

    }

    public async register(): Promise<void> {
        Object.assign(window, {
            main: this,
            ...this,

            // Add API endpoints, for ease of debugging network calls
            // Usage (in debugger console): `api.get(endpoints.user.INFO)`
            endpoints: {
                admin: AdminData,
                auth: AuthData,
                misc: MiscData,
                user: UserData
            }
        });

        await initConfig();
        await this.registerUpdateChannel();

        // Wait for creation of any injected components that require async initialization
        await Injector.init();
        console.log('Initialized');

        this.preduxStore.dispatch(new StartAuthentication());
    }

    /**
     * Creates a channel for the admin window to signal when updates have been made.
     */
    private async registerUpdateChannel(): Promise<void> {
        this._updateChannel = await fin.InterApplicationBus.Channel.create(ADMIN_UPDATE_CHANNEL);
        this._updateChannel.register(UpdateTopics.ORG_SETTINGS, () => {
            this.preduxStore.dispatch(new RefreshOrganization());
        });

        this._updateChannel.register(UpdateTopics.APPLICATIONS, async () => {
            const apps: App[] = await this.api.get(UserData.APPS, {urlParams: {scope: 'all'}});
            this.preduxStore.dispatch(new UpdateDirectory(apps));
        });

        this._updateChannel.register(UpdateTopics.SEARCH_ENGINES, async () => {
            this.preduxStore.dispatch(new GetSearchEngines());
        });
    }
}

// Start service provider
export const mainReadyPromise = async (): Promise<void> => {
    await Injector.getClass(Main).register();
};
